import apiInst from '../api.js';

/**
 * Bulk Edit Service
 *
 * Handles API operations for bulk editing product data.
 * Used by the BulkEdit view and Vuex store for batch product updates.
 */
class BulkEditService {
  constructor() {
    console.log('BulkEdit service initialized');
  }

  /**
   * Update a single product via API
   *
   * @deprecated Use batchUpdate() for bulk operations - much faster!
   *
   * Used for single product updates. For batch updates during save workflow,
   * use batchUpdate() instead which processes all products in a single API call.
   *
   * @param {String} transactionId - Product transaction ID
   * @param {Object} payload - Update payload
   * @param {Object} payload.metaData - Metadata
   * @param {Number} payload.metaData.userId - User ID
   * @param {String} payload.metaData.informationProvider - GLN
   * @param {Object} payload.productInformation - Product information
   * @param {Object} payload.productInformation.basicInformation - Basic info fields
   * @param {String} payload.productInformation.basicInformation.productBrandName - Brand name
   * @param {String} payload.productInformation.basicInformation.productName - Product name
   * @param {String} payload.productInformation.basicInformation.descriptionShort - Short description
   * @param {String} payload.productInformation.basicInformation.functionalName - Functional name
   * @param {String} payload.productInformation.basicInformation.gpcCategoryCode - GPC code
   * @param {Array} payload.productPackagingHierarchy - Packaging hierarchy
   * @returns {Promise<Object>} API response data
   * @throws {Error} If API request fails
   */
  async updateProduct(transactionId, payload) {
    try {
      const response = await apiInst.post(`/product/update/${transactionId}`, payload);
      return response.data;
    } catch (error) {
      console.error(`BULK EDIT UPDATE ERROR [${transactionId}]:`, error);
      throw error;
    }
  }

  /**
   * Batch update multiple products in a single API call
   *
   * Optimized endpoint for bulk edit operations:
   * - Single HTTP request instead of N requests
   * - Backend loads validation lookup tables once (not per product)
   * - Clear success/failure reporting per product
   *
   * Performance: 50 products in ~3-5 seconds (vs 30-60 seconds with individual calls)
   *
   * @param {Object} payload - Batch update payload
   * @param {Object} payload.metaData - Shared metadata for all products
   * @param {Number} payload.metaData.userId - User ID
   * @param {String} payload.metaData.informationProvider - GLN
   * @param {Array} payload.products - Array of products to update (max 100)
   * @param {String} payload.products[].documentId - Product transaction ID
   * @param {Object} payload.products[].productInformation - Product information
   * @param {Array} payload.products[].productPackagingHierarchy - Packaging hierarchy
   * @returns {Promise<Object>} API response with success/failure details
   * @returns {Boolean} response.success - Overall success status
   * @returns {Object} response.summary - { total, succeeded, failed }
   * @returns {Object} response.results - { succeeded: [...ids], failed: [{documentId, error}] }
   * @throws {Error} If API request fails entirely
   *
   * @example
   * const result = await bulkEditService.batchUpdate({
   *   metaData: { userId: 123, informationProvider: '8710624010003' },
   *   products: [
   *     { documentId: 'abc123', productInformation: {...}, productPackagingHierarchy: [...] },
   *     { documentId: 'def456', productInformation: {...}, productPackagingHierarchy: [...] }
   *   ]
   * });
   * // result.summary.succeeded === 2
   */
  async batchUpdate(payload) {
    try {
      const response = await apiInst.post('/product/batch-update', payload);
      return response.data;
    } catch (error) {
      console.error('BULK EDIT BATCH UPDATE ERROR:', error);
      throw error;
    }
  }

  /**
   * Load incomplete product registrations
   *
   * Fetches all products for a given information provider that have incomplete registration.
   * Used when initializing the bulk edit view.
   *
   * @param {String} informationProvider - GLN of information provider
   * @returns {Promise<Array>} Array of incomplete products
   * @throws {Error} If API request fails
   */
  async getIncompleteProducts(informationProvider) {
    try {
      const response = await apiInst.get(`/product/incompleteRegistration/${informationProvider}`);
      return response.data.data || [];
    } catch (error) {
      console.error('BULK EDIT GET INCOMPLETE ERROR:', error);
      throw error;
    }
  }

  /**
   * Get GPC category codes from backend
   *
   * Fetches list of GPC codes with Dutch descriptions for autocomplete dropdown.
   * Response format: [{code: "10005726", nameNL: "10005726 | Analoog/Digitaal Omzetters"}]
   *
   * @returns {Promise} Promise resolving to array of {code, nameNL}
   * @throws {Error} If API request fails
   */
  getGpcCodes() {
    return apiInst.get('/product/lists/BENELUX_GDSN_gpcCategoryCode');
  }

  /**
   * Ask the backend for a GPC proposal per product (max 100 rows per call).
   * Errors propagate; the store decides how to handle them.
   *
   * @param {Array<Object>} products - Rows built by buildSuggestionRequest
   * @returns {Promise<Object>} Response body { degraded, results: [...] }
   */
  async getGpcSuggestions(products) {
    const response = await apiInst.post(
      '/product/gpc-suggestions/batch',
      { products },
      { timeout: 30000 }
    );
    return response.data;
  }
}

export default new BulkEditService();
